import React from 'react';
import { FaTimes, FaCode, FaUsers, FaBrain, FaRocket, FaCrown, FaStar, FaHeart } from 'react-icons/fa';
import '../styles/JourneyModal.css';

const journeyData = [
  {
    phase: 'Year 1',
    title: 'The Curious Beginner',
    icon: <FaStar />,
    color: '#00E5FF',
    image: '/journey/year1.jpg', // replace with your actual image
    imageLabel: 'First Year — Campus Life',
    description:
      'Entered college not with a master plan — but with questions. What is coding beyond YouTube tutorials? How do people build real products? I joined clubs not because I knew what I wanted, but because I didn\'t want to miss out.',
    milestones: [
      'Joined ACM Student Chapter',
      'Started attending tech communities & events',
      'First exposure to Web3 and blockchain concepts',
      'Sat in the second row wondering what it all meant — and kept showing up anyway',
    ],
  },
  {
    phase: 'Exploration Phase',
    title: 'Trying Everything',
    icon: <FaCode />,
    color: '#7C3AED',
    image: '/journey/exploration.jpg',
    imageLabel: 'Building First Projects',
    description:
      'Tried frontend. Then backend. Then full-stack. Then Web3. Built small projects — some broke, some never hit GitHub, some were posted proudly. Somewhere between debugging errors and attending events, I realized: I loved being around builders.',
    milestones: [
      'Built first frontend projects in React',
      'Explored Node.js, Express, and MongoDB',
      'Started the MERN stack journey',
      'Experimented with Web3 and blockchain development',
    ],
  },
  {
    phase: 'Community Era',
    title: 'From Attendee to Organizer',
    icon: <FaUsers />,
    color: '#10B981',
    image: '/journey/community.jpg',
    imageLabel: 'Organizing Tech Events',
    description:
      'From attending events → volunteering → organizing → leading. I saw how events are executed, how speakers prepare, how chaos gets managed 30 minutes before start time. The same kid who sat quietly in sessions was now on stage coordinating them.',
    milestones: [
      'Volunteered at multiple tech events',
      'Joined the organizing team for community events',
      'Learned communication, ownership, and decision making',
      'Discovered that communities shape you more than classrooms do',
    ],
  },
  {
    phase: 'The Honest Chapter',
    title: 'Self-Doubt & Finding Depth',
    icon: <FaBrain />,
    color: '#F59E0B',
    image: '/journey/reflection.jpg',
    imageLabel: 'Late Night Reflections',
    description:
      'It wasn\'t all momentum. There were nights questioning everything. Everyone seemed ahead — internships, certifications, big achievements. That phase humbled me. It forced me to stop chasing noise and start choosing depth.',
    milestones: [
      'Stopped doing everything for the sake of it',
      'Started asking "why" before "what"',
      'Shifted from collecting certificates to building real understanding',
      'Learned that growth is repetitive, not viral',
    ],
  },
  {
    phase: 'Year 2 → Year 3',
    title: 'The Builder Mindset',
    icon: <FaRocket />,
    color: '#EF4444',
    image: '/journey/builder.jpg',
    imageLabel: 'Deep in the Build',
    description:
      'Something changed. I stopped learning just to post. I started learning to understand. Built a mini blockchain to understand hashes and digital signatures. Stopped debating frontend vs backend and started designing systems intentionally. Tech stopped being "cool." It became craft.',
    milestones: [
      'Built a mini blockchain from scratch',
      'Designed and shipped full-stack MERN applications',
      'Started thinking in systems, not just features',
      'Attended events for conversations, not certificates',
    ],
  },
  {
    phase: 'Leadership',
    title: 'ACM Chapter President',
    icon: <FaCrown />,
    color: '#00E5FF',
    image: '/journey/president.jpg',
    imageLabel: 'Leading the ACM Chapter',
    description:
      'From member → core team → President of the ACM Student Chapter. Not a dramatic transformation. Just showing up — again and again. This role was a reminder of how far consistency can take you.',
    milestones: [
      'Elected President of ACM Student Chapter',
      'Led a team to organize large-scale tech events',
      'Mentored juniors entering the tech community',
      'Managed clubs, code, and responsibilities — simultaneously',
    ],
  },
  {
    phase: 'Now',
    title: 'Still at the Beginning',
    icon: <FaHeart />,
    color: '#10B981',
    image: '/journey/now.jpg',
    imageLabel: 'Where I Am Today',
    description:
      'I\'m not a prodigy. Not a genius. Not "done." Just someone who didn\'t stop. From a confused first-year student to someone building, leading, experimenting — this journey isn\'t about how much I achieved. It\'s about who I became.',
    milestones: [
      'Building real-world MERN stack projects',
      'Exploring Web3 and decentralized applications',
      'Documenting and sharing the journey',
      'Still curious. Still learning. Still showing up.',
    ],
  },
];

function JourneyModal({ onClose }) {
  return (
    <div className="journey-overlay" onClick={onClose}>
      <div className="journey-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="journey-header">
          <div>
            <h2 className="journey-title">My <span className="highlight-cyan">Journey</span></h2>
            <p className="journey-subtitle">The making of a tech kid — not hype, not polished, just real.</p>
          </div>
          <button className="journey-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Timeline */}
        <div className="journey-timeline">
          {journeyData.map((item, index) => (
            <div key={index} className={`journey-item ${index % 2 === 0 ? 'left' : 'right'}`}>
              {/* Timeline dot */}
              <div className="timeline-dot" style={{ background: item.color, boxShadow: `0 0 15px ${item.color}` }}>
                {item.icon}
              </div>

              {/* Card */}
              <div className="journey-card">
                {/* Image placeholder */}
                <div className="journey-image-placeholder">
                  <img
                    src={item.image}
                    alt={item.imageLabel}
                    className="journey-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="journey-image-fallback">
                    <span className="journey-image-icon" style={{ color: item.color }}>{item.icon}</span>
                    <span className="journey-image-label">{item.imageLabel}</span>
                    <span className="journey-image-hint">Add image to /public/journey/</span>
                  </div>
                </div>

                {/* Content */}
                <div className="journey-card-content">
                  <div className="journey-phase" style={{ color: item.color }}>{item.phase}</div>
                  <h3 className="journey-card-title">{item.title}</h3>
                  <p className="journey-card-desc">{item.description}</p>
                  <ul className="journey-milestones">
                    {item.milestones.map((m, i) => (
                      <li key={i} style={{ '--dot-color': item.color }}>{m}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}

          {/* Center line */}
          <div className="timeline-line" />
        </div>
      </div>
    </div>
  );
}

export default JourneyModal;